import {
  Loader2,
  Trophy,
  Target,
  Clock,
  TrendingUp,
  DollarSign,
  Activity,
  PieChart,
} from "lucide-react";
import { useSalesAnalytics } from "../api";
import { useMarketingStore } from "../store";
import { MarketingHeader } from "../components/MarketingHeader";

function eur(n: number | null, compact = false): string {
  if (n === null) return "—";
  if (compact && Math.abs(n) >= 1000) {
    return new Intl.NumberFormat("es-ES", { notation: "compact", maximumFractionDigits: 1 }).format(n) + " €";
  }
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
}

const PALETTE = ["#0b4a2f", "#10b981", "#38bdf8", "#a78bfa", "#fbbf24", "#fb923c", "#ef4444"];

export function AnalyticsPage() {
  const { emisorId } = useMarketingStore();
  const { data, isLoading } = useSalesAnalytics(emisorId);

  return (
    <div className="p-8 flex flex-col gap-6">
      <MarketingHeader title="Marketing" subtitle="Analítica avanzada de ventas." />

      {isLoading && (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="animate-spin" size={18} /> Cargando…
        </div>
      )}

      {data && (
        <>
          {/* KPIs principales */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi label="Win rate" value={`${data.kpis.win_rate}%`} hint={`${data.kpis.ganadas} ganadas · ${data.kpis.perdidas} perdidas`} icon={Trophy} accent="emerald" />
            <Kpi label="Ticket medio" value={eur(data.kpis.ticket_medio, true)} hint="por oportunidad ganada" icon={DollarSign} accent="sky" />
            <Kpi label="Ciclo de venta" value={data.kpis.ciclo_venta_dias !== null ? `${data.kpis.ciclo_venta_dias} días` : "—"} hint="creación → cierre" icon={Clock} accent="amber" />
            <Kpi label="Valor ganado" value={eur(data.kpis.valor_ganado, true)} hint="total cerrado" icon={Target} accent="brand" />
          </div>

          {/* CAC / LTV */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft">
              <div className="text-xs font-black uppercase tracking-wider text-slate-500">CAC (coste de adquisición)</div>
              <div className="text-3xl font-black text-slate-900 mt-1">{eur(data.kpis.cac)}</div>
              <div className="text-xs text-slate-500 mt-1">
                Inversión campañas {eur(data.kpis.inversion_campanas, true)} / {data.kpis.ganadas} ventas
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft">
              <div className="text-xs font-black uppercase tracking-wider text-slate-500">LTV (valor de vida)</div>
              <div className="text-3xl font-black text-slate-900 mt-1">{eur(data.kpis.ltv)}</div>
              <div className="text-xs text-slate-500 mt-1">
                Ingresos reales / clientes facturados
              </div>
            </div>
            <div className={`rounded-2xl border p-5 shadow-soft text-white ${
              data.kpis.ltv_cac_ratio !== null && data.kpis.ltv_cac_ratio >= 3
                ? "border-brand-700/20 bg-gradient-to-br from-brand-700 to-emerald-600"
                : "border-amber-300 bg-gradient-to-br from-amber-600 to-amber-500"
            }`}>
              <div className="text-xs font-black uppercase tracking-wider opacity-90">Ratio LTV / CAC</div>
              <div className="text-3xl font-black mt-1">
                {data.kpis.ltv_cac_ratio !== null ? `${data.kpis.ltv_cac_ratio}×` : "—"}
              </div>
              <div className="text-xs opacity-80 mt-1">
                {data.kpis.ltv_cac_ratio !== null && data.kpis.ltv_cac_ratio >= 3
                  ? "Negocio saludable (≥3×)"
                  : "Objetivo recomendado: ≥ 3×"}
              </div>
            </div>
          </div>

          {/* Forecast + Ganadas por mes */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-5">
              <h2 className="font-black text-slate-900 mb-4 flex items-center gap-2">
                <TrendingUp size={18} className="text-brand-700" /> Forecast (pipeline ponderado)
              </h2>
              {data.forecast.length === 0 ? (
                <p className="text-sm text-slate-400">Sin oportunidades con fecha de cierre estimada.</p>
              ) : (
                <div className="space-y-3">
                  {data.forecast.map((f) => {
                    const max = Math.max(...data.forecast.map((x) => x.total), 1);
                    return (
                      <div key={f.mes}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-bold text-slate-700">{f.label}</span>
                          <span className="text-slate-500">
                            <span className="font-mono font-bold text-slate-900">{eur(f.ponderado, true)}</span>
                            <span className="text-xs"> de {eur(f.total, true)}</span>
                          </span>
                        </div>
                        <div className="h-3 rounded-full bg-slate-100 overflow-hidden relative">
                          <div className="h-full rounded-full bg-slate-300" style={{ width: `${(f.total / max) * 100}%` }} />
                          <div className="h-full rounded-full bg-gradient-to-r from-brand-700 to-emerald-500 absolute top-0 left-0" style={{ width: `${(f.ponderado / max) * 100}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-5">
              <h2 className="font-black text-slate-900 mb-4 flex items-center gap-2">
                <Activity size={18} className="text-brand-700" /> Ventas cerradas (12 meses)
              </h2>
              <div className="flex items-end gap-1.5 h-40">
                {data.ganadas_por_mes.map((m) => {
                  const max = Math.max(...data.ganadas_por_mes.map((x) => x.valor), 1);
                  const h = m.valor > 0 ? Math.max((m.valor / max) * 100, 3) : 0;
                  return (
                    <div key={m.mes} className="flex-1 flex flex-col items-center justify-end gap-1 h-full group" title={`${m.label}: ${eur(m.valor)} · ${m.n} ventas`}>
                      <div className="w-full rounded-t-md bg-gradient-to-t from-brand-700 to-brand-400 group-hover:from-brand-800" style={{ height: `${h}%` }} />
                      <span className="text-[10px] font-bold text-slate-500 capitalize">{m.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Embudo por etapa + atribución por fuente */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-5">
              <h2 className="font-black text-slate-900 mb-4 flex items-center gap-2">
                <Target size={18} className="text-brand-700" /> Pipeline por etapa
              </h2>
              {data.embudo_etapas.every((e) => e.n === 0) ? (
                <p className="text-sm text-slate-400">Sin oportunidades abiertas.</p>
              ) : (
                <div className="space-y-2">
                  {data.embudo_etapas.map((e) => {
                    const max = Math.max(...data.embudo_etapas.map((x) => x.total), 1);
                    return (
                      <div key={e.etapa}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-bold text-slate-700 flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full" style={{ background: e.color }} />
                            {e.etapa} <span className="text-xs text-slate-400">({e.n})</span>
                          </span>
                          <span className="font-mono font-bold text-slate-900">{eur(e.total, true)}</span>
                        </div>
                        <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${(e.total / max) * 100}%`, background: e.color }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-5">
              <h2 className="font-black text-slate-900 mb-4 flex items-center gap-2">
                <PieChart size={18} className="text-brand-700" /> Atribución de ingresos por fuente
              </h2>
              {data.atribucion_fuente.length === 0 ? (
                <p className="text-sm text-slate-400">Sin ventas atribuidas todavía.</p>
              ) : (
                <table className="w-full text-sm">
                  <tbody>
                    {data.atribucion_fuente.map((a, i) => {
                      const total = data.atribucion_fuente.reduce((s, x) => s + x.valor, 0) || 1;
                      const pct = Math.round((a.valor / total) * 100);
                      return (
                        <tr key={a.fuente} className="border-t border-slate-100">
                          <td className="py-2">
                            <span className="flex items-center gap-2 font-bold text-slate-700">
                              <span className="w-2.5 h-2.5 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />
                              {a.fuente}
                            </span>
                          </td>
                          <td className="py-2 text-right text-xs text-slate-400">{a.n} ventas</td>
                          <td className="py-2 text-right font-mono font-bold text-slate-900">{eur(a.valor, true)}</td>
                          <td className="py-2 text-right text-xs font-bold text-slate-500 w-12">{pct}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({
  label,
  value,
  hint,
  icon: Icon,
  accent,
}: {
  label: string;
  value: string;
  hint: string;
  icon: typeof Trophy;
  accent: "emerald" | "sky" | "amber" | "brand";
}) {
  const cls = {
    emerald: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-white text-emerald-700",
    sky: "border-sky-200 bg-gradient-to-br from-sky-50 to-white text-sky-700",
    amber: "border-amber-200 bg-gradient-to-br from-amber-50 to-white text-amber-700",
    brand: "border-brand-700/20 bg-gradient-to-br from-brand-700 to-emerald-600 text-white",
  }[accent];
  const isBrand = accent === "brand";
  return (
    <div className={`rounded-2xl border p-5 shadow-soft ${cls}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className={`text-xs font-black uppercase tracking-wider ${isBrand ? "text-emerald-100" : "text-slate-500"}`}>{label}</div>
          <div className={`text-2xl font-black mt-1 ${isBrand ? "text-white" : "text-slate-900"}`}>{value}</div>
          <div className={`text-xs mt-1 ${isBrand ? "text-emerald-100/80" : "text-slate-500"}`}>{hint}</div>
        </div>
        <div className={`rounded-xl p-2.5 ${isBrand ? "bg-white/15" : "bg-white/70"}`}>
          <Icon size={20} />
        </div>
      </div>
    </div>
  );
}
